import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-regular-svg-icons";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useEffect, useReducer, useState } from "react";
import {
  faAddressBook,
  faClipboardCheck,
  faCloudArrowUp,
  faGraduationCap,
  faKey,
  faShield,
  faSpinner,
  faTriangleExclamation,
  faUser,
  faUserPlus,
} from "@fortawesome/free-solid-svg-icons";
import { faAppStore, faGooglePlay, faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import axios from "axios";
import { toast } from "react-hot-toast";
import HelmetTags from "../../../MainComponents/HelmetTags.jsx";
import useAxios from "../../../MainComponents/Hooks/useAxios.jsx";

function Register({ edit, studentInfo }) {
  const [values, setValues] = useState(null);
  const { studentID } = useParams();
  const navigate = useNavigate();
  const [hide, setHide] = useState(false);
  const [governorateLoading, setGovernorateLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    getValues,
    reset,
    watch,
    setValue,
    unregister,
  } = useForm({
    mode: "onChange",
    values,
    shouldUnregister: false,
  });
  const watchGovernorate = watch("governorate");
  const watchLevel = watch("stage");
  const watchSection = watch("section");
  const watchProfilePic = watch("profile_pic");
  const selectedProfilePic = watchProfilePic?.[0];
  const [profilePreview, setProfilePreview] = useState(null);
  const showSystemType = +watchLevel === 3 && watchSection === "scientific";

  useEffect(() => {
    if (!selectedProfilePic) {
      setProfilePreview(null);
      return;
    }
    const url = URL.createObjectURL(selectedProfilePic);
    setProfilePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedProfilePic]);
  function reducer(state, action) {
    switch (action.type) {
      case "setStates": {
        return {
          ...state,
          states: action.payload,
        };
      }
      case "setGroups": {
        return {
          ...state,
          groups: action.payload,
        };
      }
      case "setSelectedState": {
        return {
          ...state,
          selectedState: action.payload,
        };
      }
      case "setErrors": {
        return {
          ...state,
          errors: action.payload,
        };
      }

      case "setHide": {
        return {
          ...state,
          hide: !state.hide,
        };
      }
      case "setSubmitLoading": {
        return {
          ...state,
          submitLoading: action.payload,
        };
      }
      case "setNoCode": {
        return {
          ...state,
          noCode: action.payload,
        };
      }
      case "setSubmitEditStudent": {
        return {
          ...state,
          submitEditStudent: action.payload,
        };
      }

      default:
        throw Error("Unknown action: " + action.type);
    }
  }

  const [state, dispatch] = useReducer(reducer, {
    hide: false,
    states: [],
    groups: [],
    selectedState: "",
    errors: {},
    submitLoading: false,
    noCode: false,
    submitEditStudent: {
      flag: "",
      dependency: false,
      data: {},
    },
  });

  const [StudentEditSuccess, StudentEditErrors, StudentEditLoading] = useAxios(
    process.env.REACT_APP_EDIT_STUDENT_INFO_API,
    "POST",
    state.submitEditStudent.flag,
    state.submitEditStudent.dependency,
    state.submitEditStudent.data,
    true,
  );
  const [GovernatesData, GovernatesErrors, GovernatesLoading] = useAxios(
    process.env.REACT_APP_GOVERNORATES_API,
    "GET",
    "GET",
  );

  useEffect(() => {
    const stateSelector = document.querySelector(".state-selector");
    const handleStateSelected = (e) => {
      dispatch({
        type: "setSelectedState",
        payload: e.target.selectedIndex,
      });
    };

    if (stateSelector) {
      stateSelector.addEventListener("change", handleStateSelected);
    }

    if (GovernatesData) {
      dispatch({
        type: "setStates",
        payload: GovernatesData?.states,
      });
      dispatch({
        type: "setGroups",
        payload: GovernatesData?.groups,
      });
    }

    return () => {
      if (stateSelector) {
        stateSelector.removeEventListener("change", handleStateSelected);
      }
    };
  }, [GovernatesData]);

  useEffect(() => {
    if (StudentEditSuccess) {
      reset();
      navigate(-1);
    }
  }, [StudentEditSuccess]);

  useEffect(() => {
    if (studentInfo?.data?.studentinfo && GovernatesData) {
      const studentData = studentInfo.data.studentinfo;

      dispatch({
        type: "setSelectedState",
        payload: studentData.selected_state,
      });

      const formData = {
        first_name: studentData.first_name || "",
        second_name: studentData.second_name || "",
        third_name: studentData.third_name || "",
        email: studentData.email || "",
        mobile: studentData.mobile || "",
        parent_mobile: studentData.parent_mobile || "",
        parent_mobile2: studentData.parent_mobile2 || "",
        governorate: studentData.governorate || "",
        area: studentData.area || "",
        stage: studentData.stage?.toString() || "",
        section: studentData.section || "",
        system_type: studentData.system_type || "",
      };

      reset(formData);
    }
  }, [studentInfo, reset, setValue, GovernatesData]);

  useEffect(() => {
    if (watchGovernorate && state.states.length > 0) {
      const selectedStateIndex = state.states.findIndex(
        (s) => s.state === watchGovernorate,
      );
      if (selectedStateIndex !== -1) {
        dispatch({
          type: "setSelectedState",
          payload: selectedStateIndex + 1,
        });
      }
    }
  }, [watchGovernorate, state.states]);

  useEffect(() => {
    if (!watchLevel) return;

    if (+watchLevel !== 3) {
      setValue("system_type", "");
      setValue("section", "");
      unregister("system_type");
      return;
    }

    if (watchSection !== "scientific") {
      setValue("system_type", "normal", { shouldValidate: true });
      unregister("system_type");
    }
  }, [watchLevel, watchSection, setValue, unregister]);

  const onSubmit = (data) => {
    const isThird = +data.stage === 3;
    const isScientificThird = isThird && data.section === "scientific";
    // Literary 3rd sec always normal — no UI choice needed
    const resolvedSystemType = isScientificThird
      ? data.system_type || "normal"
      : isThird
        ? "normal"
        : "";
    const studentData = edit
      ? {
          ...data,
          section: isThird ? data.section : "",
          system_type: resolvedSystemType,
          year: "2025",
          id: studentID,
        }
      : {
          ...data,
          profile_pic: data?.profile_pic[0] || null,
          section: isThird ? data.section : "",
          system_type: resolvedSystemType,
          year: "2025",
        };

    if (edit) {
      dispatch({
        type: "setSubmitEditStudent",
        payload: {
          flag: "AddEditStudent",
          dependency: !state.submitEditStudent.dependency,
          data: studentData,
        },
      });
    } else {
      dispatch({
        type: "setSubmitLoading",
        payload: true,
      });

      async function postData() {
        try {
          const res = await axios.post(
            process.env.REACT_APP_REGISTER_API,
            studentData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            },
          );

          const data = await res.data;
          toast.success(data.message, { duration: 50000 });
          dispatch({
            type: "setSubmitLoading",
            payload: false,
          });
          !edit && navigate("/signin");
          reset();
        } catch (error) {
          dispatch({
            type: "setErrors",
            payload: error.response.data.errors,
          });
          dispatch({
            type: "setSubmitLoading",
            payload: false,
          });
          toast.error(
            "An error has happened while creating a new account, please revise your information!",
          );
        }
      }
      postData();
    }
  };

  return (
    <section
      className={`${
        edit ? "" : "register-page"
      } flex w-full min-h-screen flex-col items-center justify-center gap-5 px-4 py-8 md:gap-4`}
    >
      {!edit && (
        <>
          <HelmetTags
            title="Create new account | Mr.Mohamed Salama"
            description="Create new account | Mr.Mohamed Salama"
            index
          ></HelmetTags>

          <div className="flex flex-col items-center justify-center gap-2">
            <img
              className="h-[48px] w-auto cursor-pointer"
              src="https://api.our-matrix.com/uploads/assets/logo.png"
              alt="logo"
              onClick={() => navigate("/")}
            />
            <h1 className="text-xl font-bold text-[#0f172a] dark:text-white">
              Sign up
            </h1>
            <p className="text-sm text-[#64748b] dark:text-slate-400">
              Enter your details and create an account.
            </p>
          </div>
        </>
      )}

      <div className={edit ? "w-full max-w-4xl" : "register-card-frame"}>
        <form
          encType="multipart/form-data"
          method="post"
          onSubmit={handleSubmit(onSubmit)}
          className="register-form"
        >
          <div className="register-section">
            <h3 className="register-section-title">
              <span className="register-section-icon" aria-hidden="true">
                <FontAwesomeIcon icon={faUser} />
              </span>
              1. Personal Information
            </h3>
            <div className="register-section-grid name-fields-grid">
              {/** First Name filed */}
              <div className="flex w-full flex-col items-start justify-center gap-1">
                <label htmlFor="first_name"> First Name</label>
            <input
              className="signin-inputs w-full "
              type="text"
              id="first_name"
              placeholder="First Name"
              name="first_name"
              autoComplete="on"
              {...register("first_name", {
                required: true,
                pattern: /^[A-Za-z]+$/,
                maxLength: 25,
                minLength: 3,
              })}
            />
            {errors.first_name && (
              <p className="field-error">
                {errors.first_name.type === "required" &&
                  "This field is required."}
                {errors.first_name.type === "pattern" &&
                  " Only English letters are allowed (Spaces not Allowed)."}
                {errors.first_name.type === "maxLength" &&
                  "Available name max length is 25."}
                {errors.first_name.type === "minLength" &&
                  " Please enter at least 3 letters."}
              </p>
            )}
            {
              //!-------server errors -----

              state.errors && state.errors.first_name && (
                <p className="field-error">{state.errors.first_name[0]}</p>
              )
            }
            {
              //!-------Editing server errors -----

              StudentEditErrors?.response?.data?.errors &&
                StudentEditErrors?.response?.data?.errors?.first_name && (
                  <p className="field-error">
                    {StudentEditErrors?.response?.data.errors.first_name[0]}
                  </p>
                )
            }
          </div>

          {/** Second Name filed */}
          <div className="flex w-full flex-col items-start justify-center gap-1">
            <label htmlFor="second_name"> Second Name</label>
            <input
              className="signin-inputs  w-full "
              type="text"
              id="second_name"
              placeholder="Second Name"
              name="second_name"
              autoComplete="on"
              {...register("second_name", {
                required: true,
                pattern: /^[A-Za-z]+$/,
                maxLength: 25,
                minLength: 3,
              })}
            />
            {errors.second_name && (
              <p className="field-error">
                {errors.second_name.type === "required" &&
                  "This field is required."}
                {errors.second_name.type === "pattern" &&
                  " Only English letters are allowed (Spaces not Allowed)."}
                {errors.second_name.type === "maxLength" &&
                  "Available name max length is 25."}
                {errors.second_name.type === "minLength" &&
                  " Please enter at least 3 letters."}
              </p>
            )}
            {
              //!-------server errors -----

              state.errors && state.errors.second_name && (
                <p className="field-error">{state.errors.second_name[0]}</p>
              )
            }
            {
              //!-------Editing server errors -----

              StudentEditErrors?.response?.data?.errors &&
                StudentEditErrors?.response?.data?.errors?.second_name && (
                  <p className="field-error">
                    {StudentEditErrors?.response?.data.errors.second_name[0]}
                  </p>
                )
            }
          </div>

          <div className="flex w-full flex-col items-start justify-center gap-1">
            <label htmlFor="third_name"> Third Name</label>
            <input
              className="signin-inputs w-full "
              type="text"
              id="third_name"
              placeholder="Third Name"
              name="third_name"
              autoComplete="on"
              {...register("third_name", {
                required: true,
                pattern: /^[A-Za-z]+$/,
                maxLength: 25,
                minLength: 3,
              })}
            />
            {errors.third_name && (
              <p className="field-error">
                {errors.third_name.type === "required" &&
                  "This field is required."}
                {errors.third_name.type === "pattern" &&
                  " Only English letters are allowed (Spaces not Allowed)."}
                {errors.third_name.type === "maxLength" &&
                  "Available name max length is 25."}
                {errors.third_name.type === "minLength" &&
                  " Please enter at least 3 letters."}
              </p>
            )}

            {
              //!-------server errors -----

              state.errors && state.errors.third_name && (
                <p className="field-error">{state.errors.third_name[0]}</p>
              )
            }
            {
              //!-------Editing server errors -----

              StudentEditErrors?.response?.data?.errors &&
                StudentEditErrors?.response?.data?.errors?.third_name && (
                  <p className="field-error">
                    {StudentEditErrors?.response?.data.errors.third_name[0]}
                  </p>
                )
            }
          </div>
        </div>

            {/** Email + Passwords */}
            <div className="register-section-grid mt-3 grid-cols-1">
          {/** Email field */}
          <div className="flex w-full flex-col items-start justify-center gap-1">
            <label htmlFor="email"> Email</label>

            <input
              className="signin-inputs  w-full "
              type="text"
              id="email"
              placeholder="Email"
              name="email"
              autoComplete="on"
              {...register("email", {
                required: true,
                pattern: /^[A-z][A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              })}
            />
            {errors.email && (
              <p className="field-error">
                {errors.email.type === "required" && "This field is required."}
                {errors.email.type === "pattern" && " Invalid Email Address."}
              </p>
            )}
            {
              //!-------server errors -----
              state.errors && state.errors.email && (
                <p className="field-error">{state.errors.email[0]}</p>
              )
            }
            {
              //!-------Editing server errors -----

              StudentEditErrors?.response?.data?.errors &&
                StudentEditErrors?.response?.data?.errors?.email && (
                  <p className="field-error">
                    {StudentEditErrors?.response?.data.errors.email[0]}
                  </p>
                )
            }
          </div>
          {!edit && (
            <div className="register-section-grid password-fields-grid">
              <div className="w-full">
                <div className="relative flex flex-col items-start justify-center gap-1">
                  <label htmlFor="password">Password</label>
                  <input
                    id="password"
                    className="signin-inputs w-full !pr-10"
                    type={`${hide ? "text" : "password"}`}
                    placeholder="Password"
                    name="password"
                    autoComplete="on"
                    {...register("password", {
                      required: true,
                      maxLength: 25,
                      minLength: 6,
                    })}
                  />
                  {hide ? (
                    <FontAwesomeIcon
                      onClick={() => setHide(!hide)}
                      className="field-icon h-4 w-4 cursor-pointer"
                      icon={faEye}
                    />
                  ) : (
                    <FontAwesomeIcon
                      onClick={() => setHide(!hide)}
                      className="field-icon h-4 w-4 cursor-pointer"
                      icon={faEyeSlash}
                    />
                  )}
                </div>
                {errors.password && (
                  <p className="field-error">
                    {errors.password.type === "required" &&
                      "This field is required."}
                    {errors.password.type === "maxLength" &&
                      "password Max length is 25 char."}
                    {errors.password.type === "minLength" &&
                      "Password Min length is 6 char."}
                  </p>
                )}
                {state.errors && state.errors.password && (
                  <p className="field-error">{state.errors.password[0]}</p>
                )}
              </div>

              <div className="w-full">
                <div className="relative flex flex-col items-start justify-center gap-1">
                  <label htmlFor="password_confirmation">
                    Confirm Password
                  </label>
                  <input
                    id="password_confirmation"
                    className="signin-inputs w-full !pr-10"
                    type={`${hide ? "text" : "password"}`}
                    placeholder="Confirm Password"
                    name="password_confirmation"
                    autoComplete="on"
                    {...register("password_confirmation", {
                      required: true,
                      maxLength: 25,
                      minLength: 6,
                      validate: (value) => value === getValues("password"),
                    })}
                  />
                  {hide ? (
                    <FontAwesomeIcon
                      onClick={() => setHide(!hide)}
                      className="field-icon h-4 w-4 cursor-pointer"
                      icon={faEye}
                    />
                  ) : (
                    <FontAwesomeIcon
                      onClick={() => setHide(!hide)}
                      className="field-icon h-4 w-4 cursor-pointer"
                      icon={faEyeSlash}
                    />
                  )}
                </div>
                {errors.password_confirmation && (
                  <p className="field-error">
                    {errors.password_confirmation.type === "required" &&
                      "This field is required."}
                    {errors.password_confirmation.type === "maxLength" &&
                      "password_confirmation Max length is 25 char."}
                    {errors.password_confirmation.type === "minLength" &&
                      "Password Min length is 6 char."}
                    {errors.password_confirmation.type === "validate" &&
                      "Passwords don't match."}
                  </p>
                )}
                {state.errors && state.errors.password_confirmation && (
                  <p className="field-error">
                    {state.errors.password_confirmation[0]}
                  </p>
                )}
              </div>
            </div>
          )}
            </div>
          </div>

          <div className="register-section">
            <h3 className="register-section-title">
              <span className="register-section-icon" aria-hidden="true">
                <FontAwesomeIcon icon={faAddressBook} />
              </span>
              2. Contact Information
            </h3>
            <div className="register-section-grid contact-fields-grid">
          {/** Student Mobile field */}

          <div className="w-full">
            <div className="flex w-full flex-col items-start justify-center gap-1">
              <label htmlFor="mobile">Student Mobile</label>
              <div className="register-input-with-icon">
                <input
                  className="signin-inputs w-full"
                  type="number"
                  inputMode="numeric"
                  id="mobile"
                  placeholder="Student Mobile"
                  name="mobile"
                  min="1"
                  autoComplete="on"
                  {...register("mobile", {
                    required: true,
                    pattern: /^[\d]{11}/,
                    maxLength: 11,
                    min: 1,
                    validate: (value) =>
                      value !== getValues("parent_mobile") &&
                      value !== getValues("parent_mobile2"),
                  })}
                />
                <FontAwesomeIcon
                  className="field-icon"
                  icon={faWhatsapp}
                />
              </div>
            </div>
            {errors.mobile && (
              <p className="pt-4 text-xs text-red-500">
                {errors.mobile.type === "required" && "This field is required."}
                {errors.mobile.type === "pattern" && " Invalid Mobile Number."}
                {errors.mobile.type === "maxLength" &&
                  " Invalid Mobile Number."}
                {errors.mobile.type === "min" && " Invalid Mobile Number."}
                {errors.mobile.type === "validate" &&
                  "Please, Don't Enter the Same Mobile Number."}
              </p>
            )}
            {
              //!-------server errors -----

              state.errors && state.errors.mobile && (
                <p className="pt-2 text-xs text-red-500">
                  {state.errors.mobile[0]}
                </p>
              )
            }
            {
              //!-------Editing server errors -----

              StudentEditErrors?.response?.data?.errors &&
                StudentEditErrors?.response?.data?.errors?.mobile && (
                  <p className="pt-2 text-xs text-red-500">
                    {StudentEditErrors?.response?.data.errors.mobile[0]}
                  </p>
                )
            }
          </div>
          {/** Father's Mobile field */}

          <div className="w-full">
            <div className="flex w-full flex-col items-start justify-center gap-1">
              <label htmlFor="parent_mobile">Father's Mobile</label>
              <div className="register-input-with-icon">
                <input
                  className="signin-inputs w-full"
                  type="number"
                  inputMode="numeric"
                  id="parent_mobile"
                  placeholder="Father's Mobile"
                  name="parent_mobile"
                  autoComplete="on"
                  min="1"
                  {...register("parent_mobile", {
                    required: true,
                    pattern: /^[\d]{11}/,
                    maxLength: 11,
                    min: 1,
                    validate: (value) =>
                      value !== getValues("mobile") &&
                      value !== getValues("parent_mobile2"),
                  })}
                />
                <FontAwesomeIcon
                  className="field-icon"
                  icon={faWhatsapp}
                />
              </div>
            </div>
            {errors.parent_mobile && (
              <p className="pt-4 text-xs text-red-500">
                {errors.parent_mobile.type === "required" &&
                  "This field is required."}
                {errors.parent_mobile.type === "pattern" &&
                  " Invalid Mobile Number."}
                {errors.parent_mobile.type === "maxLength" &&
                  " Invalid Mobile Number."}
                {errors.parent_mobile.type === "min" &&
                  " Invalid Mobile Number."}
                {errors.parent_mobile.type === "validate" &&
                  "Please, Don't Enter the Same Mobile Number."}
              </p>
            )}
            {
              //!-------server errors -----

              state.errors && state.errors.parent_mobile && (
                <p className="pt-2 text-xs text-red-500">
                  {state.errors.parent_mobile[0]}
                </p>
              )
            }
            {
              //!-------Editing server errors -----

              StudentEditErrors?.response?.data?.errors &&
                StudentEditErrors?.response?.data?.errors?.parent_mobile && (
                  <p className="pt-2 text-xs text-red-500">
                    {StudentEditErrors?.response?.data.errors.parent_mobile[0]}
                  </p>
                )
            }
          </div>
          {/** Mother's Mobile field */}

          <div className="w-full">
            <div className="flex w-full flex-col items-start justify-center gap-1">
              <label htmlFor="parent_mobile2">Mother's Mobile</label>
              <div className="register-input-with-icon">
                <input
                  className="signin-inputs w-full"
                  type="number"
                  inputMode="numeric"
                  id="parent_mobile2"
                  placeholder="Mother's Mobile"
                  name="parent_mobile2"
                  autoComplete="on"
                  min="1"
                  {...register("parent_mobile2", {
                    required: true,
                    pattern: /^[\d]{11}/,
                    maxLength: 11,
                    min: 1,
                    validate: (value) =>
                      value !== getValues("mobile") &&
                      value !== getValues("parent_mobile"),
                  })}
                />
                <FontAwesomeIcon
                  className="field-icon"
                  icon={faWhatsapp}
                />
              </div>
            </div>
            {errors.parent_mobile2 && (
              <p className="pt-4 text-xs text-red-500">
                {errors.parent_mobile2.type === "required" &&
                  "This field is required."}
                {errors.parent_mobile2.type === "pattern" &&
                  " Invalid Mobile Number."}
                {errors.parent_mobile2.type === "maxLength" &&
                  " Invalid Mobile Number."}
                {errors.parent_mobile2.type === "min" &&
                  " Invalid Mobile Number."}
                {errors.parent_mobile2.type === "validate" &&
                  "Please, Don't Enter the Same Mobile Number."}
              </p>
            )}
            {
              //!-------server errors -----

              state.errors && state.errors.parent_mobile2 && (
                <p className="pt-2 text-xs text-red-500">
                  {state.errors.parent_mobile2[0]}
                </p>
              )
            }
            {
              //!-------Editing server errors -----

              StudentEditErrors?.response?.data?.errors &&
                StudentEditErrors?.response?.data?.errors?.parent_mobile2 && (
                  <p className="pt-2 text-xs text-red-500">
                    {StudentEditErrors?.response?.data.errors.parent_mobile2[0]}
                  </p>
                )
            }
          </div>
            </div>
          </div>

          <div className="register-section">
            <h3 className="register-section-title">
              <span className="register-section-icon" aria-hidden="true">
                <FontAwesomeIcon icon={faGraduationCap} />
              </span>
              3. Location & Academic Info
            </h3>
            <div className="register-section-grid grid-cols-1 md:grid-cols-3">
          {/** Governorate filed */}

          <div className="flex w-full flex-col items-start justify-center gap-1">
            <label htmlFor="governorate">Governorate</label>
            <select
              className="state-selector"
              name="governorate"
              id="governorate"
              {...register("governorate", { required: true })}
            >
              <option hidden value=""></option>
              {state.states.map((state) => (
                <option key={state.id} value={state.state}>
                  {state.state}
                </option>
              ))}
            </select>
            {errors.governorate && (
              <p className="pt-2 text-xs text-red-500">
                {errors.governorate.type === "required" &&
                  "This field is required."}
              </p>
            )}
            {
              //!-------server errors -----

              state.errors && state.errors.governorate && (
                <p className="text-xs text-red-500  ">
                  {state.errors.governorate[0]}
                </p>
              )
            }{" "}
            {
              //!-------Editing server errors -----

              StudentEditErrors?.response?.data?.errors &&
                StudentEditErrors?.response?.data?.errors?.governorate && (
                  <p className="pt-2 text-xs text-red-500">
                    {StudentEditErrors?.response?.data.errors.governorate[0]}
                  </p>
                )
            }
          </div>

          {/** City filed */}
          <div className="flex w-full flex-col items-start justify-center gap-1">
            {watchGovernorate ? (
              <>
                <label htmlFor="area">City</label>
                <select
                  name="area"
                  id="area"
                  {...register("area", { required: true })}
                >
                  {state.states[state.selectedState - 1]?.cities.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.city_name_en}
                    </option>
                  ))}
                </select>

                {errors.area && (
                  <p className="text-xs text-red-500  ">
                    {errors.area.type === "required" &&
                      "This field is required."}
                  </p>
                )}

                {
                  //!-------server errors -----

                  state.errors && state.errors.area && (
                    <p className="text-xs text-red-500  ">
                      {state.errors.area[0]}
                    </p>
                  )
                }
                {
                  //!-------Editing server errors -----

                  StudentEditErrors?.response?.data?.errors &&
                    StudentEditErrors?.response?.data?.errors?.area && (
                      <p className="pt-2 text-xs text-red-500">
                        {StudentEditErrors?.response?.data.errors.area[0]}
                      </p>
                    )
                }
              </>
            ) : (
              ""
            )}
          </div>

          {/** Stage Level  */}
          <div className="flex w-full flex-col items-start justify-center gap-1">
            <label htmlFor="stage">Stage</label>
            <select
              className="register-select"
              name="stage"
              id="stage"
              {...register("stage", { required: true })}
              defaultValue=""
            >
              <option value="" disabled hidden>
                Select stage
              </option>
              <option value="1">1st sec</option>
              <option value="2">2nd sec</option>
              <option value="3">3rd sec</option>
            </select>
            {errors.stage && (
              <p className="field-error">
                {errors.stage.type === "required" && "This field is required."}
              </p>
            )}
            {state.errors && state.errors.stage && (
              <p className="field-error">{state.errors.stage[0]}</p>
            )}
            {StudentEditErrors?.response?.data?.errors &&
              StudentEditErrors?.response?.data?.errors?.stage && (
                <p className="field-error">
                  {StudentEditErrors?.response?.data.errors.stage[0]}
                </p>
              )}
          </div>
        </div>

        {+watchLevel === 3 && (
          <div className="register-section-grid section-system-grid">
            <div className="flex w-full flex-col items-start gap-1">
              <label>Section</label>
              <div className="radio-row">
                <div className="flex items-center gap-2">
                  <input
                    className="cursor-pointer"
                    type="radio"
                    id="scientific"
                    value="scientific"
                    {...register("section", { required: true })}
                  />
                  <label className="cursor-pointer" htmlFor="scientific">
                    Scientific
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    className="cursor-pointer"
                    type="radio"
                    id="literary"
                    value="literary"
                    {...register("section", { required: true })}
                  />
                  <label className="cursor-pointer" htmlFor="literary">
                    Literary
                  </label>
                </div>
              </div>
              {errors.section && (
                <p className="field-error">
                  {errors.section.type === "required" &&
                    "This field is required."}
                </p>
              )}
              {state.errors && state.errors.section && (
                <p className="field-error">{state.errors.section[0]}</p>
              )}
              {StudentEditErrors?.response?.data?.errors &&
                StudentEditErrors?.response?.data?.errors?.section && (
                  <p className="field-error">
                    {StudentEditErrors?.response?.data.errors.section[0]}
                  </p>
                )}
            </div>

            {showSystemType && (
              <div className="flex w-full flex-col items-start gap-1">
                <label>System</label>
                <div className="radio-row">
                  <div className="flex items-center gap-2">
                    <input
                      className="cursor-pointer"
                      type="radio"
                      id="hand_by_hand"
                      value="hand_by_hand"
                      {...register("system_type", {
                        required: showSystemType,
                      })}
                    />
                    <label className="cursor-pointer" htmlFor="hand_by_hand">
                      ايد بايد
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      className="cursor-pointer"
                      type="radio"
                      id="normal_system"
                      value="normal"
                      {...register("system_type", {
                        required: showSystemType,
                      })}
                    />
                    <label className="cursor-pointer" htmlFor="normal_system">
                      النظام العادي
                    </label>
                  </div>
                </div>
                {errors.system_type && (
                  <p className="field-error">
                    {errors.system_type.type === "required" &&
                      "This field is required."}
                  </p>
                )}
                {state.errors && state.errors.system_type && (
                  <p className="field-error">{state.errors.system_type[0]}</p>
                )}
                {StudentEditErrors?.response?.data?.errors &&
                  StudentEditErrors?.response?.data?.errors?.system_type && (
                    <p className="field-error">
                      {StudentEditErrors?.response?.data.errors.system_type[0]}
                    </p>
                  )}
              </div>
            )}
          </div>
        )}
          </div>

          {!edit && (
            <div className="register-section">
              <h3 className="register-section-title">
                <span className="register-section-icon" aria-hidden="true">
                  <FontAwesomeIcon icon={faKey} />
                </span>
                4. Account Activation
              </h3>
              <div className="register-section-grid grid-cols-1 md:grid-cols-2">
            <div className="flex w-full flex-col items-start justify-center gap-1">
              <label htmlFor="activation_code">
                Activation Code{" "}
                <span className="font-normal text-[#64748b]">
                  (كود التفعيل 200 ج)
                </span>
              </label>
              <input
                className="signin-inputs w-full "
                type="text"
                id="activation_code"
                placeholder="Activation Code"
                name="activation_code"
                autoComplete="on"
                {...register("activation_code", {
                  required: true,
                })}
              />
              {errors.activation_code && (
                <p className="field-error">
                  {errors.activation_code.type === "required" &&
                    "This field is required."}
                </p>
              )}
              {state.errors && state.errors.activation_code && (
                <p className="field-error">{state.errors.activation_code[0]}</p>
              )}
              {StudentEditErrors?.response?.data?.errors &&
                StudentEditErrors?.response?.data?.errors?.activation_code && (
                  <p className="field-error">
                    {
                      StudentEditErrors?.response?.data.errors
                        .activation_code[0]
                    }
                  </p>
                )}
            </div>
            <div className="profile-upload flex w-full flex-col gap-1">
              <span className="profile-upload__label">Profile Picture</span>
              <label
                htmlFor="profile_pic"
                className={`profile-upload__dropzone ${
                  selectedProfilePic ? "has-file" : ""
                }`}
              >
                <input
                  id="profile_pic"
                  className="profile-upload__input"
                  type="file"
                  accept="image/jpg, image/jpeg, image/png, image/webp"
                  name="profile_pic"
                  {...register("profile_pic", {
                    required: true,
                    validate: (value) => !(value[0]?.size > 5000000),
                  })}
                />
                {profilePreview ? (
                  <img
                    src={profilePreview}
                    alt="Profile preview"
                    className="profile-upload__preview"
                  />
                ) : (
                  <span className="profile-upload__icon" aria-hidden="true">
                    <FontAwesomeIcon icon={faCloudArrowUp} />
                  </span>
                )}
                <span className="profile-upload__text">
                  <strong>
                    {selectedProfilePic
                      ? selectedProfilePic.name
                      : "Choose profile picture"}
                  </strong>
                  <small>
                    {selectedProfilePic
                      ? "Click to change image"
                      : "PNG, JPG, WEBP — Max 5MB"}
                  </small>
                </span>
              </label>
              {errors.profile_pic && (
                <p className="field-error">
                  {errors.profile_pic.type === "required" &&
                    "This field is required."}
                  {errors.profile_pic.type === "validate" &&
                    "Max Image Size is 5MB."}
                </p>
              )}
              {state.errors && state.errors.profile_pic && (
                <p className="field-error">{state.errors.profile_pic[0]}</p>
              )}
            </div>
              </div>
            </div>
          )}

          {!edit && (
            <div className="register-section">
              <h3 className="register-section-title">
                <span className="register-section-icon" aria-hidden="true">
                  <FontAwesomeIcon icon={faClipboardCheck} />
                </span>
                5. Instructions & Confirmation
              </h3>
              <div className="instructions-panel">
                <p className="instructions-heading">
                  تعليمات هامة جدا للطالب
                </p>
                <div className="instructions-body" dir="rtl">
                  <p>
                    ١ - بداية العمل يوم 15 / 8 للصف الثالث الثانوي، أما بالنسبة
                    لأولى وتانية بكالوريا لم يتم التحديد بعد
                  </p>
                  <p>
                    ٢ - المنصة عبارة عن ويب سايت يعمل فقط من خلال متصفح جوجل كروم،
                    ولن يعمل من أي متصفح آخر – التفعيل من خلال فودافون كاش والأرقام
                    على صفحة الفيسبوك ومتوفر في السناتر
                  </p>
                  <p>
                    ٣ - يمكن الدخول للمنصة من الموبايل أو اللاب توب – لا يُستحب
                    الدخول من تابلت مهكر
                  </p>
                  <p>
                    ٤ - إذا كنت تستخدم آيفون، يجب تحميل جوجل كروم، لأن المنصة لا
                    تعمل على متصفح سفاري أو من متصفح الفيس بوك
                  </p>
                  <p>
                    ٥ - عند إنشاء الحساب يجب كتابة البيانات بشكل صحيح (اسمك رباعي
                    – رقم الواتساب – رقم ولي الأمر)
                  </p>
                  <p>٦ - مسموح للطالب باستخدام المنصة من جهاز واحد فقط</p>
                  <p>
                    ٧ - كل فيديو متاح للمشاهدة ٣ مرات فقط، لذا تأكد من جودة
                    الإنترنت قبل التشغيل
                  </p>
                  <p>٨ - يتم احتساب المشاهدة بعد مرور 30% من وقت الفيديو</p>
                  <p>
                    ٩ - يتم إرسال تقرير دوري بالمستوى لولي الأمر عن طريق تطبيق ولي
                    الأمر{" "}
                    <span dir="ltr" className="instructions-brand">
                      (Our Matrix)
                    </span>{" "}
                    والواتساب
                  </p>
                  <p className="instructions-store-links" dir="rtl">
                    <span className="instructions-store-caption">
                      تقدر تحمّل الابلكيشن من هنا
                    </span>
                    <span className="instructions-store-buttons" dir="ltr">
                      <a
                        className="instructions-store-link"
                        href="https://play.google.com/store/apps/details?id=com.parent.ourmatrix"
                        target="_blank"
                        rel="noreferrer"
                      >
                        <FontAwesomeIcon icon={faGooglePlay} />
                        <span>Google Play</span>
                      </a>
                      <a
                        className="instructions-store-link"
                        href="https://apps.apple.com/sa/app/our-matrix/id6743887804"
                        target="_blank"
                        rel="noreferrer"
                      >
                        <FontAwesomeIcon icon={faAppStore} />
                        <span>App Store</span>
                      </a>
                    </span>
                  </p>
                  <p>
                    ١٠ - يجب عدم شراء أكواد أكثر من الحاجة، ولا يتم استرجاع
                    الأكواد
                  </p>
                  <p>
                    ١١ - يتم نزول حصة يوم السبت ويوم الثلاثاء من كل أسبوع. على
                    الطالب التسجيل في حصة السبت (يوم السبت والانتهاء منها قبل يوم
                    الحذف يوم الاثنين الساعة ١١:٥٩ مساءً) وفي حالة عدم الانتهاء
                    وتسليم الواجب يتم حذف الأكونت أوتوماتيك نهائياً (والمستر صارم
                    في هذا البند ولن يسمح بعودة الطالب مرة أخرى). وكذلك بالنسبة
                    لحصة يوم الثلاثاء على الطالب الانتهاء وتسليم الواجب قبل يوم
                    الخميس الساعة ١١:٥٩ مساءً
                  </p>
                  <p>
                    ١٢ - يوم الجمعة يتم نزول الحصتين لمن يريد إعادة فيديو ما (يوم
                    الجمعة فقط ويتم رفع الحصص الساعة ١١:٥٩ مساءً)
                  </p>
                  <p>١٣ - الحصص إجبارية ومرتبة ولن تتمكن من تخطي أي حصة</p>
                  <p>١٤ - يتم الشرح من خلال كتب خاصة تُباع في السناتر</p>
                  <div className="deletion-warning" dir="rtl">
                    <div className="deletion-warning__header">
                      <span className="deletion-warning__icon" aria-hidden="true">
                        <FontAwesomeIcon icon={faTriangleExclamation} />
                      </span>
                      <div>
                        <strong>١٥ - حالات حذف الحساب</strong>
                        <small>تنبيه هام: الحساب يُحذف نهائياً في الحالات التالية</small>
                      </div>
                    </div>
                    <ul className="deletion-warning__list">
                      <li>عدم دخول حصة في الفترة المحددة لها</li>
                      <li>عدم دخول الامتحانات الشهرية</li>
                      <li>إدخال أرقام أولياء أمور غير صحيحة</li>
                      <li>
                        حدوث تجاوز مع الدعم الفني أو العلمي او مع ارقام فودافون
                        كاش (فودافون كاش بيتم الرد في خلال 24 ساعه بالترتيب من
                        الاقدم للأحدث)
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="confirmation-check mt-4 flex w-full flex-col gap-1">
                <div className="flex items-center gap-3">
                  <input
                    className=""
                    type="checkbox"
                    id="checked"
                    name="checked"
                    autoComplete="on"
                    {...register("checked", {
                      required: true,
                      maxLength: 15,
                      minLength: 3,
                    })}
                  />
                  <label
                    className="flex items-center gap-2 text-start !text-[0.8rem]"
                    htmlFor="checked"
                  >
                    Accept All terms & conditions (اقبل جميع الشروط والآحكام)
                  </label>
                </div>
                {errors.checked && (
                  <p className="field-error">
                    {errors.checked.type === "required" &&
                      "Please accept terms & conditions"}
                  </p>
                )}
                {state.errors && state.errors.checked && (
                  <p className="field-error">{state.errors.checked[0]}</p>
                )}
                {StudentEditErrors?.response?.data?.errors &&
                  StudentEditErrors?.response?.data?.errors?.checked && (
                    <p className="field-error">
                      {StudentEditErrors?.response?.data.errors.checked[0]}
                    </p>
                  )}
              </div>
            </div>
          )}

          <button
            disabled={!isValid || state.submitLoading}
            className="submit"
            type="submit"
          >
            {state.submitLoading ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : edit ? (
              "Edit Student"
            ) : (
              <>
                <FontAwesomeIcon icon={faUserPlus} />
                <span>Register</span>
              </>
            )}
          </button>
        </form>
      </div>
      {!edit && (
        <p className="text-sm text-[#64748b] dark:text-slate-400">
          Already have an account?{" "}
          <Link className="font-bold text-[#0341dd]" to="/signin">
            Sign in.
          </Link>
        </p>
      )}
    </section>
  );
}

export default Register;
